import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Calendar, MapPin, ExternalLink, Loader, CheckCircle, Phone, Send } from 'lucide-react';
import api from '../../utils/api';
import { getDishDisplayName } from '../../utils/dishDisplay';
import { DEFAULT_AVATAR } from '../../utils/constants';
import './ContributorDetails.scss';

const ContributorDetails = () => {
    const { email } = useParams();
    const navigate = useNavigate();
    const [contributor, setContributor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showEmailOverlay, setShowEmailOverlay] = useState(false);
    const [sendingType, setSendingType] = useState(null);
    const [emailError, setEmailError] = useState(null);
    const [emailSuccess, setEmailSuccess] = useState(null);

    const [year] = useState(2026);

    useEffect(() => {
        if (email) {
            fetchContributor();
        }
    }, [email]);

    const fetchContributor = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/admin/contributors/${encodeURIComponent(email)}`, {
                params: { year }
            });
            if (response.data.success) {
                setContributor(response.data.contributor);
            }
        } catch (err) {
            console.error('Error fetching contributor:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return '—';
        return new Date(date).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const handleSendEmail = async (type) => {
        const contributorEmail = contributor?.email ?? contributor?._id ?? email;
        if (!contributorEmail || sendingType) return;
        setSendingType(type);
        setEmailError(null);
        setEmailSuccess(null);
        try {
            const res = await api.post(`/admin/contributors/${encodeURIComponent(contributorEmail)}/send-email`, { type }, {
                params: { year }
            });
            if (res.data.success) {
                setEmailSuccess(type === 'impact-summary' ? 'Impact summary email sent!' : 'Contribute more email sent!');
            }
        } catch (err) {
            setEmailError(err.response?.data?.message || 'Failed to send email');
        } finally {
            setSendingType(null);
        }
    };

    const closeOverlay = () => {
        setShowEmailOverlay(false);
        setEmailError(null);
        setEmailSuccess(null);
    };

    if (loading) {
        return (
            <div className="contributor-details loading-state">
                <Loader size={32} className="spinner" />
                <p>Loading contributor...</p>
            </div>
        );
    }

    if (!contributor) {
        return (
            <div className="contributor-details empty-state">
                <p>Contributor not found</p>
                <button className="back-button" onClick={() => navigate('/contributors')}>
                    <ArrowLeft size={18} />
                    Back to Contributors
                </button>
            </div>
        );
    }

    const sortedMosques = [...(contributor.mosques || [])].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
    );

    return (
        <div className="contributor-details">
            <button className="back-button" onClick={() => navigate('/contributors')}>
                <ArrowLeft size={18} />
                Back to Contributors
            </button>

            <div className="contributor-header">
                <div className="avatar-wrap">
                    <img
                        src={contributor.picture || DEFAULT_AVATAR}
                        alt={contributor.name}
                        className="contributor-avatar"
                        onError={(e) => {
                            e.target.src = DEFAULT_AVATAR;
                        }}
                    />
                </div>
                <div className="contributor-meta">
                    <h1>{contributor.name || 'Unknown'}</h1>
                    <div className="meta-row">
                        <Mail size={18} />
                        <a href={`mailto:${contributor.email}`}>{contributor.email}</a>
                    </div>
                    {contributor.phone && (
                        <div className="meta-row">
                            <Phone size={18} />
                            <a href={`tel:${contributor.phone}`}>{contributor.phone}</a>
                        </div>
                    )}
                    <div className="stats-row">
                        <div className="stat-card">
                            <span className="stat-value">{contributor.totalMosques}</span>
                            <span className="stat-label">🕌 Total Mosques</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-value">{contributor.verifiedMosques || 0}</span>
                            <span className="stat-label">Verified</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-value">{contributor.totalVisits ?? 0}</span>
                            <span className="stat-label">👁️ Total Views</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-value">{contributor.totalDirectionClicks ?? 0}</span>
                            <span className="stat-label">📤 Directions Clicked</span>
                        </div>
                    </div>
                    <button
                        className="send-email-btn"
                        onClick={() => setShowEmailOverlay(true)}
                        title="Send email to contributor"
                    >
                        <Send size={18} />
                        Send Email
                    </button>
                </div>
            </div>

            <div className="section">
                <h2>Contributed Mosques</h2>
                {sortedMosques.length === 0 ? (
                    <p className="no-mosques">No mosques contributed yet.</p>
                ) : (
                    <div className="mosques-list">
                        {sortedMosques.map((mosque) => (
                            <div key={mosque.mosqueId} className="mosque-card">
                                <div className="mosque-card-main">
                                    <div className="mosque-info">
                                        <h3>{mosque.name}</h3>
                                        <div className="mosque-meta">
                                            <span className="district">
                                                <MapPin size={14} />
                                                {mosque.district}
                                            </span>
                                            <span className="dish">{getDishDisplayName(mosque.dish)}</span>
                                            <span className="date">
                                                <Calendar size={14} />
                                                {formatDate(mosque.date)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mosque-actions">
                                        {mosque.verified && (
                                            <span className="verified-badge">
                                                <CheckCircle size={16} />
                                                Verified
                                            </span>
                                        )}
                                        <button
                                            className="view-button"
                                            onClick={() => navigate(`/mosques/${mosque.mosqueId}`)}
                                        >
                                            View
                                        </button>
                                        {mosque.googleMapsLink && (
                                            <a
                                                href={mosque.googleMapsLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="maps-link"
                                            >
                                                <ExternalLink size={16} />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showEmailOverlay && (
                <div className="email-overlay" onClick={closeOverlay}>
                    <div className="email-overlay__content" onClick={(e) => e.stopPropagation()}>
                        <div className="email-overlay__header">
                            <h3>Send Email to Contributor</h3>
                            <button type="button" className="email-overlay__close" onClick={closeOverlay}>×</button>
                        </div>
                        <div className="email-overlay__options">
                            <div className="email-option">
                                <h4>1. Your contributions making impact</h4>
                                <p>Send a summary of their stats (mosques, views, directions) with a button to view their profile on the client site.</p>
                                <button
                                    className="email-option__btn"
                                    onClick={() => handleSendEmail('impact-summary')}
                                    disabled={sendingType !== null}
                                >
                                    {sendingType === 'impact-summary' ? 'Sending...' : 'Send Impact Summary'}
                                </button>
                            </div>
                            <div className="email-option">
                                <h4>2. Ask to contribute more (in Malayalam)</h4>
                                <p>Ask them to add more mosques in their area. Email is in Malayalam with English translation.</p>
                                <button
                                    className="email-option__btn"
                                    onClick={() => handleSendEmail('contribute-more')}
                                    disabled={sendingType !== null}
                                >
                                    {sendingType === 'contribute-more' ? 'Sending...' : 'Send Contribute More'}
                                </button>
                            </div>
                        </div>
                        {emailError && <div className="email-overlay__error">{emailError}</div>}
                        {emailSuccess && <div className="email-overlay__success">{emailSuccess}</div>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContributorDetails;
